import React from 'react';
import { AlertTriangle, Plus, Minus, Pencil } from 'lucide-react';
import { Button, Modal } from '../ui/index.js';
import type { FieldDiff } from '../../hooks/useWorkflowForm.js';

interface PendingChangesModalProps {
  open: boolean;
  onClose: () => void;
  onDiscard: () => void;
  diffs: FieldDiff[];
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  const str = String(value);
  return str || '(empty)';
}

function truncate(str: string, max: number): string {
  return str.length > max ? str.slice(0, max) + '...' : str;
}

// `FieldDiff.previous`/`.current` are `unknown` (they cover both scalar top-level fields and
// whole step objects) — for a step diff (`diff.path` starting with `steps.`) the runtime value is
// a step-shaped record; narrow with a real check rather than trusting a cast.
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** These fields are `string | null` on WorkflowStep — render as '' for anything else, matching
 * the previous `(x as any)?.field` behavior for a non-string/absent value. */
function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

const DiffCard: React.FC<{ diff: FieldDiff }> = ({ diff }) => {
  const isStep = diff.path.startsWith('steps.');
  const icon =
    diff.type === 'added' ? <Plus size={14} /> : diff.type === 'removed' ? <Minus size={14} /> : <Pencil size={14} />;
  const color = diff.type === 'added' ? 'text-success' : diff.type === 'removed' ? 'text-danger' : 'text-warning';
  const bgColor =
    diff.type === 'added' ? 'bg-success-muted' : diff.type === 'removed' ? 'bg-danger-muted' : 'bg-warning-muted';

  return (
    <div className={`rounded-lg border p-3 ${bgColor}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={color}>{icon}</span>
        <span className="text-sm font-medium text-heading">{diff.label}</span>
        <span className="text-xs text-faint capitalize">({diff.type})</span>
      </div>
      {diff.type === 'changed' && !isStep && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-faint">Previous:</span>
            <pre className="mt-1 p-1.5 rounded-sm bg-surface text-body whitespace-pre-wrap wrap-break-word">
              {truncate(formatValue(diff.previous), 200)}
            </pre>
          </div>
          <div>
            <span className="text-faint">Current:</span>
            <pre className="mt-1 p-1.5 rounded-sm bg-surface text-body whitespace-pre-wrap wrap-break-word">
              {truncate(formatValue(diff.current), 200)}
            </pre>
          </div>
        </div>
      )}
      {diff.type === 'changed' && isStep && (
        <StepDiffDetail
          previous={isRecord(diff.previous) ? diff.previous : null}
          current={isRecord(diff.current) ? diff.current : null}
        />
      )}
      {diff.type === 'added' && isStep && isRecord(diff.current) && (
        <div className="text-xs text-body">
          <span className="text-muted">Template:</span> {asString(diff.current.templateSlug) || 'Not set'}
          {asString(diff.current.description) && (
            <span className="ml-2 text-muted"> — {truncate(asString(diff.current.description), 80)}</span>
          )}
        </div>
      )}
      {diff.type === 'removed' && isStep && isRecord(diff.previous) && (
        <div className="text-xs text-body">
          <span className="text-muted">Was:</span> {asString(diff.previous.name) || 'Unnamed'} (
          {asString(diff.previous.templateSlug) || 'No template'})
        </div>
      )}
    </div>
  );
};

const StepDiffDetail: React.FC<{
  previous: Record<string, unknown> | null;
  current: Record<string, unknown> | null;
}> = ({ previous, current }) => {
  if (!previous || !current) return null;
  const fields = ['name', 'description', 'templateSlug', 'executionMode', 'optional', 'order'];
  const changed = fields.filter((f) => {
    const p = previous[f],
      c = current[f];
    return p !== c && (p !== undefined || c !== undefined);
  });
  if (changed.length === 0) return <div className="text-xs text-muted">Configuration changed</div>;

  return (
    <div className="space-y-1 text-xs">
      {changed.map((field) => (
        <div key={field} className="flex gap-2">
          <span className="text-muted w-24 shrink-0 capitalize">{field}:</span>
          <span className="text-danger line-through">{truncate(formatValue(previous[field]), 60)}</span>
          <span className="text-faint">&rarr;</span>
          <span className="text-success">{truncate(formatValue(current[field]), 60)}</span>
        </div>
      ))}
    </div>
  );
};

export const PendingChangesModal: React.FC<PendingChangesModalProps> = ({ open, onClose, onDiscard, diffs }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Pending changes (${diffs.length} change${diffs.length !== 1 ? 's' : ''})`}
      titleIcon={<AlertTriangle size={18} className="text-warning" />}
      maxWidth="2xl"
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              onDiscard();
              onClose();
            }}
          >
            Discard all changes
          </Button>
          <Button onClick={onClose}>Continue editing</Button>
        </div>
      }
    >
      <div className="space-y-3">
        {diffs.length === 0 ? (
          <p className="text-sm text-muted">No changes detected.</p>
        ) : (
          diffs.map((diff) => <DiffCard key={diff.path} diff={diff} />)
        )}
      </div>
    </Modal>
  );
};
